import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ParametersActions from './parameter-by-detail.actions';
import { catchError, map, mergeMap, withLatestFrom, tap, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { ParametersApiService } from './parameter-by-detail.api.service';
import { selectRequestedParameters } from './parameter-by-detail.selectors';
import { ParametersDataResponseRequest } from 'src/app/pages/admin-manager/parameters/models/parameters.model';

function isParameterEqual(a: ParametersDataResponseRequest, b: ParametersDataResponseRequest): boolean {
  return (
    a.parameterCode === b.parameterCode && a.parameterScopeTypeId === b.parameterScopeTypeId && a.scopeId === b.scopeId
  );
}

@Injectable()
export class ParametersEffects {
  constructor(
    private actions$: Actions,
    private api: ParametersApiService,
    private store: Store,
  ) {}

  loadParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParametersActions.loadParameterDetails),
      withLatestFrom(this.store.select(selectRequestedParameters)),
      map(([{ request }, requested]) => {
        if (!requested) {
          return { toRequest: request };
        }
        const toRequest = request.filter((param) => !requested.some((req) => isParameterEqual(param, req)));
        return { toRequest };
      }),
      filter(({ toRequest }) => toRequest?.length > 0),
      tap(({ toRequest }) => {
        this.store.dispatch(ParametersActions.saveRequestedParameters({ request: toRequest }));
      }),
      mergeMap(({ toRequest }) => {
        return this.api.getMultipleParametersByDetails(toRequest).pipe(
          map((response) => ParametersActions.loadParametersSuccess({ response, request: toRequest })),
          catchError((error) => of(ParametersActions.loadParametersFailure({ error, request: toRequest }))),
        );
      }),
    ),
  );

  retryLoadParameters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParametersActions.retryLoadParameters),
      withLatestFrom(this.store.select(selectRequestedParameters)),
      filter(([_, requested]) => requested.length > 0),
      mergeMap(([_, requested]) =>
        this.api.getMultipleParametersByDetails(requested).pipe(
          map((response) => ParametersActions.loadParametersSuccess({ response, request: requested })),
          catchError((error) => of(ParametersActions.loadParametersFailure({ error, request: requested }))),
        ),
      ),
    ),
  );

  refreshEndpoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ParametersActions.refreshLoadParameters),
      withLatestFrom(this.store.select(selectRequestedParameters)),
      filter(([_, requested]) => requested.length > 0),
      mergeMap(([_, requested]) =>
        this.api.getMultipleParametersByDetails(requested).pipe(
          map((response) => ParametersActions.refreshLoadParametersSuccess({ response, request: requested })),
          catchError((error) => of(ParametersActions.loadParametersFailure({ error, request: requested }))),
        ),
      ),
    ),
  );
}
